<template>
  <div>
    <div>
      <ActitiveTabcomponent :tabs="mainTabs" :defaultTab="'product'" @tab-changed="setActiveType">
        <template #tab-buttons>
          <v-icon plain @click="cancel" class="cursor-pointer hover:bg-gray-300 rounded p-1">
            mdi-close
          </v-icon>
        </template>
        <template #product>
          <div class="bg-white dark-bg-custom rounded-lg">
            <v-form ref="form" v-model="valid" lazy-validation>
              <div class="p-4">
                <v-text-field
                  v-model="productName"
                  label="Product Name"
                  outlined
                  dense
                  required
                  :rules="[(v) => !!v || `Product Name is required`]"
                  hide-details
                  class="mb-4"
                ></v-text-field>

                <v-text-field
                  v-model="productDescription"
                  label="Product Description"
                  outlined
                  dense
                  hide-details
                  class="mb-4"
                ></v-text-field>

                <v-combobox
                  flat
                  :menu-props="{ offsetY: true }"
                  v-model="selectedMaker"
                  :items="partners"
                  label="Search Brand"
                  placeholder="Search Brand"
                  @update:search-input="debounceMethod"
                  :search-input.sync="searchPartner"
                  item-text="partnerName"
                  item-value="_id"
                  outlined
                  multiple
                  dense
                  hide-details
                ></v-combobox>
              </div>
            </v-form>
          </div>
        </template>
        <template #bulk>
          <GenericBulkUpload
            ref="bulkUpload"
            title="Bulk Upload Products"
            subtitle="Upload multiple products using Excel file"
            entityName="Product"
            :requiredColumns="['SL No.', 'Product Name', 'Product Description', 'Brand']"
            @save-button-visibility="handleSaveButtonVisibility"
            :sampleData="[
              {
                'SL No.': 1,
                'Product Name': 'Smartphone X1',
                'Product Description': 'Latest smartphone with advanced features',
                Brand: 'TechBrand'
              },
              {
                'SL No.': 2,
                'Product Name': 'Laptop Pro',
                'Product Description': 'High-performance laptop for professionals',
                Brand: 'CompTech'
              }
            ]"
            :uploadEndpoint="axiosUrl.products.productBulkUpload"
            method="post"
            successKey="productsCreatedSuccessfully"
            failedKey="productsCreationFailed"
            invalidItemsKey="invalidProducts"
            :enableSave="false"
            :controlParentSaveButton="true"
            @close="cancel"
            @uploadComplete="onBulkUploadComplete"
          />
        </template>
      </ActitiveTabcomponent>
    </div>
    <div class="flex justify-end p-4 border-t">
      <v-btn text class="mr-4 normal-case" @click="cancel"> Cancel </v-btn>
      <v-btn
        v-if="isSaveButtonEnabled"
        class="bg-[#2A83FF] white--text normal-case"
        :color="$vuetify.theme.currentTheme.primary"
        @click="handleSave"
      >
        <v-icon left>mdi-content-save</v-icon>
        Save
      </v-btn>
    </div>
  </div>
</template>
<script>
import { sortDropdownOptions, debounce } from '@/utils/common'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent'
import GenericBulkUpload from '@/components/Workforce/CollectionManagement/GenericBulkUpload.vue'
import { axiosUrl } from '@/utils/workforceApi'

export default {
  data() {
    return {
      productName: '',
      productDescription: '',
      searchPartner: '',
      selectedMaker: [],
      partners: [],
      activeType: 'product',
      isSaveButtonEnabled: true,
      valid: true
    }
  },
  components: {
    ActitiveTabcomponent,
    GenericBulkUpload
  },
  props: {
    editProductData: {
      type: Object,
      default: null
    }
  },
  created() {
    if (this.editProductData) {
      this.productName = this.editProductData.productName || ''
      this.productDescription = this.editProductData.productDescription || ''
      this.selectedMaker = this.editProductData.productMaker.map((el) => el.maker)
    }
  },
  computed: {
    mainTabs() {
      const tabs = [
        {
          label: this.editProductData ? 'Edit product' : 'Add product',
          key: 'product',
          icon: this.editProductData ? 'mdi-pencil' : 'mdi-plus',
          component: 'AddProduct'
        },
        {
          label: this.editProductData ? 'Bulk upload' : 'Bulk Create',
          key: 'bulk',
          icon: 'mdi-upload',
          component: 'GenericBulkUpload'
        }
      ]
      return tabs
    }
  },
  methods: {
    setActiveType(type) {
      this.activeType = type
      if (type === 'product') {
        this.isSaveButtonEnabled = true
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    onBulkUploadComplete(data) {
      this.$toast.success('Bulk upload completed successfully!')
      this.cancel()
    },
    debounceMethod: debounce(function (text) {
      if (!text && !this.editProductData) {
        return
      }
      this.fetchPartners()
    }, 800),
    async fetchPartners() {
      try {
        const response = await this.$axios.get(axiosUrl.partners.getPartners, {
          params: {
            search: this.searchPartner
          }
        })
        const partners = response.data.partners

        this.partners = sortDropdownOptions(partners, 'partnerName')
      } catch (error) {
        console.error('An error occurred:', error)
      }
    },
    handleSaveButtonVisibility(shouldShow) {
      if (this.activeType === 'bulk') {
        this.isSaveButtonEnabled = shouldShow
      }
    },
    handleSave() {
      if (this.activeType === 'product') {
        this.saveProduct()
      } else if (this.activeType === 'bulk') {
        this.saveBulkProducts()
      }
    },
    async saveProduct() {
      if (this.$refs.form) {
        const isValid = this.$refs.form.validate()
        if (!isValid) return
      }

      try {
        let apiEndpoint
        let method
        let message
        if (this.editProductData) {
          apiEndpoint = `${axiosUrl.products.saveProduct}/${this.editProductData._id}`
          method = 'put'
          message = 'updated'
        } else {
          apiEndpoint = `${axiosUrl.products.saveProduct}`
          method = 'post'
          message = 'created'
        }

        const data = this.selectedMaker.map((el) => {
          return {
            maker: el,
            model: []
          }
        })

        const payload = {
          productName: this.productName,
          productDescription: this.productDescription || undefined,
          productMaker: data
        }
        await this.$axios[method](apiEndpoint, payload)
        this.$toast.success(`Product ${message} successfully !!`)
        this.cancel()
      } catch (error) {
        console.log('An error occurred:', error)
        this.$toast.error(`Product ${error?.message} failed !!`)
      }
    },
    saveBulkProducts() {
      if (!this.$refs.bulkUpload) {
        console.error('Bulk upload component not found')
        this.$toast.error('Bulk upload component not found')
        return
      }
      if (!this.$refs.bulkUpload.selectedFile) {
        this.$toast.error('Please select a file first')
        return
      }
      console.log('Bulk upload state:', {
        selectedFile: this.$refs.bulkUpload.selectedFile,
        validationPassed: this.$refs.bulkUpload.validationPassed,
        validating: this.$refs.bulkUpload.validating,
        validationMessages: this.$refs.bulkUpload.validationMessages
      })
      if (this.$refs.bulkUpload.validationPassed) {
        try {
          console.log('Processing file upload...')
          this.$refs.bulkUpload.processFile()
        } catch (error) {
          console.error('Error processing file:', error)
          this.$toast.error('Error processing file: ' + (error.message || 'Unknown error'))
        }
        return
      }
      if (this.$refs.bulkUpload.validating) {
        this.$toast.info('Validating file, please wait...')
        const checkValidation = setInterval(() => {
          if (!this.$refs.bulkUpload.validating) {
            clearInterval(checkValidation)
            if (this.$refs.bulkUpload.validationPassed) {
              try {
                console.log('Processing file upload...')
                this.$refs.bulkUpload.processFile()
              } catch (error) {
                console.error('Error processing file:', error)
                this.$toast.error('Error processing file: ' + (error.message || 'Unknown error'))
              }
            } else {
              this.$toast.error(
                'Please ensure the file is valid before uploading. Check that all required columns are present.'
              )
            }
          }
        }, 500)
        setTimeout(() => {
          clearInterval(checkValidation)
          if (this.$refs.bulkUpload.validating) {
            this.$toast.error('Validation is taking too long. Please try again.')
          }
        }, 1000)
        return
      }
      this.$toast.info('Validating file...')
      this.$refs.bulkUpload
        .validateFile()
        .then(() => {
          if (this.$refs.bulkUpload.validationPassed) {
            try {
              console.log('Processing file upload...')
              this.$refs.bulkUpload.processFile()
            } catch (error) {
              console.error('Error processing file:', error)
              this.$toast.error('Error processing file: ' + (error.message || 'Unknown error'))
            }
          } else {
            this.$toast.error(
              'Please ensure the file is valid before uploading. Check that all required columns are present.'
            )
          }
        })
        .catch((error) => {
          console.error('Error during validation:', error)
          this.$toast.error('Error validating file: ' + (error.message || 'Unknown error'))
        })
    }
  }
}
</script>
